export const AGENT_STATUSES = ["draft", "running", "paused", "error", "stopped"];

export const AGENT_SEVERITIES = ["info", "warn", "critical"];

export const AGENT_TRIGGER_SOURCES = ["workflow_event", "webhook", "polling", "schedule", "internal"];

export const AGENT_ACTION_TYPES = [
  "send_sms",
  "send_slack",
  "create_record",
  "update_record",
  "run_workflow_step",
  "run_ai_task",
];

export const createId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export const createDefaultPolicy = () => ({
  allowed_apps: [],
  allowed_actions: [],
  max_actions_per_hour: 30,
  max_notifications_per_hour: 30,
  requires_approval_for: [],
  escalation_channels: [],
  quiet_hours: {
    enabled: false,
    start: "22:00",
    end: "07:00",
    timezone: "UTC",
  },
  kill_switch_error_streak: 5,
});

export const createDefaultTrigger = () => ({
  trigger_id: createId("trigger"),
  source: "workflow_event",
  type: "",
  filter: "",
  dedupe_key: "",
  enabled: true,
  /** Optional: event payload from trigger test (used as sandbox/test run input). */
  test_data: null,
});

export const createDefaultAction = () => ({
  action_id: createId("action"),
  type: "send_slack",
  label: "",
  config: {},
});

export const createDefaultRule = (actionId = null) => ({
  rule_id: createId("rule"),
  name: "",
  when: "event",
  if: "",
  action_ids: actionId ? [actionId] : [],
  else_action_ids: [],
  severity: "warn",
  cooldown_minutes: 5,
  enabled: true,
});

export const createDefaultAgentState = () => ({
  state_json: {},
  state_version: 1,
  last_event_at: null,
  last_tick_at: null,
  error_streak: 0,
  last_error: null,
});

const safeStorage = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    if (v == null || String(v) === "null" || String(v) === "undefined") return fallback;
    return String(v);
  } catch {
    return fallback;
  }
};

export const createDefaultAgent = (overrides = {}) => {
  return {
    agent_id: createId("agent"),
    tenant_id: safeStorage("tenant", "default_tenant"),
    name: "Untitled Agent",
    description: "",
    goal: "",
    status: "draft",
    created_by_user_id: safeStorage("user", "unknown_user"),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    policy: createDefaultPolicy(),
    state: createDefaultAgentState(),
    triggers: [],
    actions: [],
    rules: [],
    knowledge_sources: [],
    /** Durable lessons from past runs (injected into the brain on later previews/live runs). */
    run_memory: [],
    /** ISO timestamp of last successful Preview (required before Start while status is draft). */
    previewed_at: null,
    ...overrides,
  };
};

/**
 * Build a scrubbed agent document for cloning into another tenant.
 * - Keeps name, goal, instructions, policy, knowledge, lessons, trigger/action/rule structure
 * - Mints new agent/trigger/action/rule ids (remaps rule action_ids + lesson action_ids)
 * - Clears credentials, webhook urls, workflow_id, run history, preview gate, runtime state
 * - Preserves source created_by_user_id; sets tenant_id to destination
 */
export const buildClonedAgentPayload = (sourceAgent, destinationTenantId) => {
  const source = sourceAgent && typeof sourceAgent === "object" ? sourceAgent : {};
  const actionIdMap = {};

  const scrubCredentialFields = (obj) => {
    if (!obj || typeof obj !== "object") return obj;
    return { ...obj, credential: "", authname: "" };
  };

  const scrubConfig = (config) => {
    if (!config || typeof config !== "object" || Array.isArray(config)) return config || {};
    return { ...config, credential: "", authname: "" };
  };

  const actions = (Array.isArray(source.actions) ? source.actions : []).map((action) => {
    const oldId = action?.action_id;
    const newId = createId("action");
    if (oldId) actionIdMap[String(oldId)] = newId;
    const scrubbed = scrubCredentialFields({ ...action });
    return {
      ...scrubbed,
      action_id: newId,
      config: scrubConfig(action?.config),
    };
  });

  const triggers = (Array.isArray(source.triggers) ? source.triggers : []).map((trigger) => {
    const scrubbed = scrubCredentialFields({ ...trigger });
    return {
      ...scrubbed,
      trigger_id: createId("trigger"),
      // Destination must re-register webhooks / schedules after connecting credentials.
      url: "",
      registered_message: "",
      create_trigger_message: "",
      stop_trigger_message: "",
    };
  });

  const remapActionIds = (ids) =>
    (Array.isArray(ids) ? ids : [])
      .map((id) => actionIdMap[String(id)] || null)
      .filter(Boolean);

  const rules = (Array.isArray(source.rules) ? source.rules : []).map((rule) => ({
    ...rule,
    rule_id: createId("rule"),
    action_ids: remapActionIds(rule?.action_ids),
    else_action_ids: remapActionIds(rule?.else_action_ids),
  }));

  const run_memory = (Array.isArray(source.run_memory) ? source.run_memory : []).map((lesson) => {
    if (!lesson || typeof lesson !== "object") return lesson;
    const next = { ...lesson };
    if (next.action_id != null && next.action_id !== "") {
      next.action_id = actionIdMap[String(next.action_id)] || "";
    }
    return next;
  });

  const policy =
    source.policy && typeof source.policy === "object"
      ? { ...createDefaultPolicy(), ...source.policy }
      : createDefaultPolicy();

  const ts = new Date().toISOString();
  const createdBy =
    source.created_by_user_id != null &&
    String(source.created_by_user_id).trim() !== "" &&
    String(source.created_by_user_id) !== "null" &&
    String(source.created_by_user_id) !== "undefined"
      ? String(source.created_by_user_id)
      : safeStorage("user", "unknown_user");

  const payload = createDefaultAgent({
    name: source.name || "Untitled Agent",
    description: source.description || "",
    goal: source.goal || "",
    instructions: source.instructions || "",
    status: "draft",
    tenant_id: String(destinationTenantId || "").trim() || safeStorage("tenant", "default_tenant"),
    created_by_user_id: createdBy,
    created_at: ts,
    updated_at: ts,
    policy,
    state: createDefaultAgentState(),
    triggers,
    actions,
    rules,
    knowledge_sources: Array.isArray(source.knowledge_sources)
      ? JSON.parse(JSON.stringify(source.knowledge_sources))
      : [],
    run_memory,
    previewed_at: null,
    cloned_from_agent_id: source.agent_id || undefined,
  });
  delete payload.workflow_id;
  return payload;
};
